import { ClassUserType, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { store } from "store/index";
import { selectIsClassSessionVirtual, selectSectionCapabilities } from "store/selectors";
import { selectCurrentListenedBreakoutGroupId } from "store/selectors/breakouts";
import { AutoMuteScenario, localStateActions } from "store/slices/local";
import { toastSuccessMessage } from "utils/alerts";

/**
 * Unified service to handle automatic muting of users in all scenarios.
 * This replaces both frontend and backend auto-mute logic.
 */
export class AutoMuteService {
  /**
   * Check if section allows auto-muting students
   */
  static sectionAllowsAutoMute(section: SectionType | null): boolean {
    return section?.mute_students_on_main_room_entry === true;
  }

  /**
   * Check if user should be auto-muted
   */
  static shouldAutoMute(section: SectionType | null): boolean {
    const state = store.getState();
    const { canParticipateInSection } = selectSectionCapabilities(state);
    const isClassSessionVirtual = selectIsClassSessionVirtual(state);
    const isListeningToBreakout = selectCurrentListenedBreakoutGroupId(state) !== null;

    // Mute if listening to breakout, should only apply to instructors
    if (isListeningToBreakout) {
      return true;
    }

    if (!this.sectionAllowsAutoMute(section)) {
      return false;
    }

    if (!isClassSessionVirtual) {
      return false;
    }

    return canParticipateInSection;
  }

  /**
   * Get the appropriate notification message for a scenario
   */
  static getNotificationMessage(scenario: AutoMuteScenario): string | null {
    switch (scenario) {
      case "initial_join":
        return t("notifications.auto_muted_main_room");
      case "refresh":
        return t("notifications.auto_muted_on_refresh");
      case "breakout_return":
        return t("notifications.auto_muted_from_breakout");
      case "breakout_listening":
        return null; // No notification when auto-muting for breakout listening
      default:
        return t("notifications.auto_muted_main_room");
    }
  }

  /**
   * Apply auto-mute by updating store state and showing notification.
   * Audio device state is managed by AvProvider.
   */
  static applyAutoMute(scenario: AutoMuteScenario): void {
    store.dispatch(localStateActions.setLocalMicIsEnabled(false));
    store.dispatch(localStateActions.setWasAutoMuted(true));
    store.dispatch(localStateActions.setLastAutoMuteScenario(scenario));
    const message = this.getNotificationMessage(scenario);
    if (message) {
      toastSuccessMessage(message);
    }
  }

  /**
   * Unified auto-mute handler for all scenarios
   */
  static handleAutoMute(section: SectionType | null, scenario: AutoMuteScenario): void {
    if (this.shouldAutoMute(section)) {
      this.applyAutoMute(scenario);
    }
  }

  /**
   * Detect the current auto-mute scenario
   */
  static detectScenario(
    classUser: ClassUserType | null,
    previousBreakoutGroupId: string | null,
  ): AutoMuteScenario {
    const currentBreakoutGroupId = classUser?.breakout_group_id || null;
    const currentListeningBreakoutGroupId = classUser?.listened_breakout_group_id || null;

    if (currentListeningBreakoutGroupId !== null) {
      return "breakout_listening";
    }

    if (previousBreakoutGroupId !== null && currentBreakoutGroupId === null) {
      return "breakout_return";
    }

    if (classUser?.is_in_class_session === true) {
      return "refresh";
    }

    return "initial_join";
  }
}
